import { useState } from 'react';
import { Bell, ExternalLink } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import { instructorApi, type ActiveAlert } from '@/lib/api';
import { cn } from '@/lib/utils';

function formatRelative(iso?: string): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const deltaSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (deltaSec < 60) return `${deltaSec}s ago`;
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`;
  return `${Math.floor(deltaSec / 3600)}h ago`;
}

function severityClass(sev?: string): string {
  switch ((sev ?? '').toLowerCase()) {
    case 'critical':
      return 'bg-destructive/15 text-destructive border-destructive/30';
    case 'warning':
      return 'bg-yellow-500/15 text-yellow-700 border-yellow-500/30';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}

interface AlertBellProps {
  /** Override polling interval — used in tests so we don't background-fetch forever. */
  pollIntervalMs?: number;
}

export function AlertBell({ pollIntervalMs = 5_000 }: AlertBellProps) {
  const [open, setOpen] = useState(false);

  const alerts = useQuery({
    queryKey: ['instructor-alerts'],
    queryFn: async () => (await instructorApi.alerts()).data,
    refetchInterval: pollIntervalMs,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const active = (alerts.data?.active ?? []) as ActiveAlert[];
  const count = active.length;
  const hasAlerts = count > 0;

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={hasAlerts ? `${count} active alerts` : 'No active alerts'}
        className="relative"
        onClick={() => setOpen(true)}
        data-testid="alert-bell"
        data-active={hasAlerts ? 'true' : 'false'}
      >
        <Bell
          className={cn(
            'h-5 w-5',
            hasAlerts ? 'text-destructive' : 'text-(--color-ink-muted)',
          )}
        />
        {hasAlerts && (
          <Badge
            variant="destructive"
            data-testid="alert-bell-count"
            className="absolute -top-1 -right-1 h-5 min-w-5 px-1 text-xs flex items-center justify-center"
          >
            {count}
          </Badge>
        )}
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" data-testid="alert-bell-drawer">
          <SheetHeader>
            <SheetTitle>Active alerts</SheetTitle>
            <SheetDescription>
              {hasAlerts
                ? `${count} firing — Grafana webhook → catalog → this UI.`
                : 'No alerts firing right now.'}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 flex-1 overflow-y-auto space-y-3" role="list">
            {!hasAlerts && (
              <p className="text-sm text-muted-foreground">
                When chaos toggles trip an alert rule, it appears here within ~5s.
              </p>
            )}
            {active.map((a, i) => {
              const title = a.title || a.name || a.uid || 'alert';
              return (
                <div
                  key={a.uid ?? `${title}-${i}`}
                  role="listitem"
                  data-testid="alert-item"
                  className="rounded-md border bg-card p-3 shadow-xs"
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display text-sm font-semibold">{title}</h3>
                    <span
                      className={cn(
                        'rounded border px-2 py-0.5 text-xs font-medium uppercase',
                        severityClass(a.severity),
                      )}
                    >
                      {a.severity ?? 'info'}
                    </span>
                  </div>
                  {a.summary && (
                    <p className="mt-1 text-sm text-muted-foreground">{a.summary}</p>
                  )}
                  <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{formatRelative(a.firedAt)}</span>
                    {a.runbookUrl && (
                      <a
                        href={a.runbookUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        View runbook
                        <ExternalLink className="h-3 w-3" aria-hidden="true" />
                      </a>
                    )}
                  </div>
                  {a.runbook && (
                    <details className="mt-2 text-xs">
                      <summary className="cursor-pointer font-medium text-primary hover:underline">
                        Runbook
                      </summary>
                      {/* Plain text only — the backend annotation happens to look
                          like markdown, but we render it as-is (pre-wrap, no
                          markdown parsing, no dangerouslySetInnerHTML). */}
                      <div className="mt-1 whitespace-pre-wrap text-muted-foreground">
                        {a.runbook}
                      </div>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
